import React, { useState, useEffect } from 'react';
import { useAutoTranslation } from '../hooks/useAutoTranslation';

interface AutoTranslateTextProps {
  children: string;
  sourceLang?: string;
  fallback?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  showLoadingIndicator?: boolean;
  showErrorIndicator?: boolean;
}

const AutoTranslateText: React.FC<AutoTranslateTextProps> = ({
  children,
  sourceLang = 'fr',
  fallback,
  className,
  style,
  disabled = false,
  showLoadingIndicator = true,
  showErrorIndicator = true
}) => {
  const { translateText, isTranslating, currentLanguage } = useAutoTranslation();
  const [translatedText, setTranslatedText] = useState<string>(children);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isCurrentlyTranslating, setIsCurrentlyTranslating] = useState<boolean>(false);

  useEffect(() => {
    const performTranslation = async (): Promise<void> => {
      if (disabled || !children || currentLanguage === sourceLang) {
        setTranslatedText(children);
        return;
      }

      setIsCurrentlyTranslating(true);
      
      try {
        setHasError(false);
        const translated = await translateText(children, currentLanguage);
        setTranslatedText(translated);
      } catch (error) {
        console.error('Erreur traduction composant:', error);
        setHasError(true);
        setTranslatedText(fallback || children);
      } finally {
        setIsCurrentlyTranslating(false);
      }
    };

    performTranslation();
  }, [children, currentLanguage, sourceLang, disabled, translateText, fallback]);

  if (showLoadingIndicator && (isCurrentlyTranslating || (isTranslating && translatedText === children))) {
    return (
      <span className={className} style={style}>
        {children}
        <span style={{ 
          opacity: 0.5, 
          fontSize: '0.8em', 
          marginLeft: '4px',
          color: '#6c757d'
        }}>
          🔄
        </span>
      </span>
    );
  }

  if (showErrorIndicator && hasError) {
    return (
      <span 
        className={className} 
        style={style} 
        title="Erreur de traduction - Affichage du texte original"
      >
        {fallback || children}
        <span style={{ 
          opacity: 0.5, 
          fontSize: '0.8em', 
          marginLeft: '4px',
          color: '#dc3545'
        }}>
          ⚠️
        </span>
      </span>
    );
  }

  return (
    <span className={className} style={style}>
      {translatedText}
    </span>
  );
};

export default AutoTranslateText;